// =============================================================================
// ROLL PROTOCOL — system-agnostic dice resolution seam
// =============================================================================
// [CREATED: 2026-05-30 — Sub-plan 1H-G-bw Stone 1]
// [PURPOSE]: Single seam through which all UsableAbilityModal dice resolution
//   flows. Parameterised on `protocol` so the same caller code works for 5e
//   (d20 standard), Daggerheart (duality dice), and any future rule system.
//
// [WHY ONE SEAM]: Without this, every ability handler that calls rollDice
//   directly becomes system-coupled. The modal passes rollProtocol down; the
//   protocol switch here is the ONLY place that needs to change when a new
//   system ships its dice model.
//
// [ENDGAME — Daggerheart]: When Daggerheart ships, remove the stub from the
//   'dualityDice' branch and implement 2d12 Hope+Fear roll. The return shape
//   { totals, breakdowns, protocol, hopeRoll?, fearRoll? } already reserves
//   the Daggerheart fields so callers do not need to update their contracts.
//
// [BREADCRUMB — 'custom' protocol]: A 'custom' entry-point accepts a
//   `customRoller` function in `spec`. Intended for homebrew rule systems that
//   author their own resolution function through the Rule System Editor
//   (endgame Sub-plan 3D). The entry-point is wired but not surfaced in any UI
//   until a consumer is authored.
//
// [ENDGAME — 'spellSlot' cost type in consumeTriggeredSource]: The `type:'spellSlot'`
//   branch in consumeTriggeredSource.js is D&D-specific. When Daggerheart ships
//   the RSE, generalize it to `type:'pool'` with the Hope/Fear pool as target
//   (Gen-6 master-queue row). This file does not use spellSlot directly; note
//   is here because rollProtocol is the resolution seam that would carry the
//   cost-type alongside the roll spec.
//
// [RULE 22]: Tested at src/__tests__/usableAbilityModal/usableAbilityProtocol.test.js T1+T2.
// [RULE 25]: 'dualityDice' stub is breadcrumbed; 'custom' entry-point is breadcrumbed.
//
// API:
//   executeRollProtocol(spec, protocol?) → { totals, breakdowns, protocol, ...extraFields }
//
//   spec: {
//     rolls: [{ dice: string, label: string }],  // e.g. [{dice:'2d10', label:'fire damage'}]
//     customRoller?: (spec) => result,            // only used when protocol='custom'
//   }
//   protocol: 'd20' | 'dualityDice' | 'custom'  (default: 'd20')
//
//   Returns:
//   {
//     totals:     number[],   // one entry per rolls[] element
//     breakdowns: string[],   // human-readable breakdown per roll (e.g. "2d10(4+7)=11")
//     labels:     string[],   // original labels from spec.rolls, echoed back
//     protocol:   string,     // the protocol used
//     // dualityDice fields (undefined on d20):
//     hopeRoll?:  number | null,
//     fearRoll?:  number | null,
//     withHope?:  boolean,
//     withFear?:  boolean,
//     stub?:      boolean,    // true when the branch is a stub (not yet implemented)
//   }

import { rollDice } from './diceParser';

/**
 * Execute a dice roll according to the specified protocol.
 *
 * @param {Object} spec - Roll specification
 * @param {Array<{dice:string, label:string}>} spec.rolls - Dice groups to roll
 * @param {Function} [spec.customRoller] - Custom roller (used with protocol='custom')
 * @param {string} [protocol='d20'] - Roll protocol identifier
 * @returns {{ totals, breakdowns, labels, protocol, ...extraFields }}
 */
export function executeRollProtocol(spec, protocol = 'd20') {
  const rolls = Array.isArray(spec?.rolls) ? spec.rolls : [];

  switch (protocol) {
    case 'd20':
    case 'default': {
      // Standard d20 resolution — used by 5e and 5.5e.
      // Each roll entry in spec.rolls is evaluated via diceParser.rollDice.
      const totals = [];
      const breakdowns = [];
      const labels = [];

      for (const roll of rolls) {
        const notation = roll?.dice || '1d20';
        const result = rollDice(notation);
        totals.push(result.total);
        breakdowns.push(result.breakdown || `${result.total}`);
        labels.push(roll?.label || notation);
      }

      return {
        totals,
        breakdowns,
        labels,
        protocol: 'd20',
      };
    }

    case 'dualityDice': {
      // [BREADCRUMB — Daggerheart duality dice seam — Sub-plan 3D / Daggerheart arc]
      // Implementation: 2d12, one die designated "Hope" and the other "Fear".
      // The total is hopeRoll + fearRoll. If hopeRoll > fearRoll the roll is
      // "with Hope" (positive narrative momentum); otherwise "with Fear".
      // Full implementation deferred to the Daggerheart system arc.
      // When that arc ships: remove stub:true, compute real 2d12 rolls here,
      // and populate hopeRoll + fearRoll + withHope + withFear.
      console.warn(
        '[rollProtocol] dualityDice protocol is a stub — Daggerheart arc not yet shipped. ' +
        'Returns placeholder values only.'
      );
      return {
        totals: [],
        breakdowns: [],
        labels: [],
        protocol: 'dualityDice',
        hopeRoll: null,
        fearRoll: null,
        withHope: undefined,
        withFear: undefined,
        stub: true,
      };
    }

    case 'custom': {
      // [BREADCRUMB — Rule System Editor custom roller — Sub-plan 3D]
      // A Rule System Editor rule system can supply a customRoller function
      // in the spec. When the RSE ships, this branch calls spec.customRoller(spec)
      // and returns its result. For now the entry-point is wired but the RSE is
      // not yet built so the custom roller falls through to the d20 path as a
      // safe default.
      if (typeof spec?.customRoller === 'function') {
        const result = spec.customRoller(spec);
        return { ...result, protocol: 'custom' };
      }
      // Fall through to d20 as safe default until RSE ships.
      console.warn(
        '[rollProtocol] custom protocol — no customRoller provided; falling back to d20.'
      );
      return executeRollProtocol(spec, 'd20');
    }

    default: {
      console.warn(
        `[rollProtocol] Unknown protocol "${protocol}" — falling back to d20.`
      );
      return executeRollProtocol(spec, 'd20');
    }
  }
}

export default executeRollProtocol;
